import ChannelSelector from "@components/ChannelSelector";
import ExportButton from "@components/ExportButton";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import PeriodFilter, { type PeriodRange } from "@components/PeriodFilter";
import ToggleButtonGroup from "@components/ToggleButtonGroup";
import { type ChannelMeterRow, useChannelsFlat, useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { type NetworkRow, useNetworkData } from "@features/reports/api";
import { buildExportUrl } from "@shared/buildExportUrl";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useDefaultEmail } from "@shared/useDefaultEmail";
import { useMailComposer } from "@shared/useMailComposer";
import { useStandardPagination } from "@shared/useStandardPagination";
import { Alert, Select, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";

const parameterGroups: Record<
  string,
  {
    label: string;
    keys: string[];
  }
> = {
  all: {
    label: "Все величины",
    keys: [
      "freq",
      "ia",
      "ib",
      "ic",
      "i0",
      "ua",
      "ub",
      "uc",
      "uab",
      "ubc",
      "uac",
      "wa",
      "wb",
      "wc",
      "vara",
      "varb",
      "varc",
      "vaa",
      "vab",
      "vac",
      "pfanga",
      "pfangb",
      "pfangc",
      "v_pfa",
      "v_pfb",
      "v_pfc",
      "phangb",
      "phangc",
    ],
  },
  currents: { label: "Токи", keys: ["ia", "ib", "ic", "i0"] },
  voltages: { label: "Фазные напряжения", keys: ["ua", "ub", "uc"] },
  line: { label: "Линейные напряжения", keys: ["uab", "ubc", "uac"] },
  power: { label: "Мощность", keys: ["wa", "wb", "wc", "vara", "varb", "varc"] },
  pf: {
    label: "Коэффициент мощности",
    keys: ["pfanga", "pfangb", "pfangc", "v_pfa", "v_pfb", "v_pfc"],
  },
  angles: { label: "Углы", keys: ["phangb", "phangc"] },
};

const toggleOptions = [
  { label: "С учётом Кт×Кн", value: "transformer" },
  { label: "Линейное напряжение", value: "line" },
  { label: "Профильные данные", value: "profile" },
];

const formatNumber = (value: number | null | undefined, digits = 3) => {
  if (value === undefined || value === null) {
    return "—";
  }
  return value.toFixed(digits);
};

const ReportsNetworkPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [selectedChannel, setSelectedChannel] = useState<number>();
  const [parameterGroup, setParameterGroup] = useState<string>("all");
  const [toggles, setToggles] = useState<string[]>([]);
  const [period, setPeriod] = useState<PeriodRange>(() => {
    const start = dayjs().startOf("day");
    return { from: start, to: start.add(1, "day") };
  });

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;

  const channelsQuery = useChannelsFlat(objectID, undefined, 500);
  const channelOptions = useMemo(() => {
    return (channelsQuery.data ?? []).map((channel: ChannelMeterRow) => ({
      label: `${channel.meter_number} — ${channel.feeder_name}`,
      value: channel.meter_number,
      description: channel.channel_label,
    }));
  }, [channelsQuery.data]);

  useEffect(() => {
    if (selectedChannel && channelOptions.some((option) => option.value === selectedChannel)) {
      return;
    }
    if (channelOptions.length > 0) {
      setSelectedChannel(channelOptions[0].value);
    } else {
      setSelectedChannel(undefined);
    }
  }, [channelOptions, selectedChannel]);

  const fromISO = period.from?.format("YYYY-MM-DD");
  const toISO = period.to?.format("YYYY-MM-DD");
  const useProfile = toggles.includes("profile");
  const useTransformer = toggles.includes("transformer");
  const showLineVoltage = toggles.includes("line");

  const networkQuery = useNetworkData({
    region: regionID,
    object: objectID,
    channel: selectedChannel,
    from: fromISO,
    to: toISO,
    parameter: parameterGroup,
    useProfile,
  });

  const data = networkQuery.data;
  const rows = data?.rows ?? [];
  const labels = data?.labels ?? {};
  const transformerRatio = data?.transformer_ratio ?? 1;
  const pagination = useStandardPagination(rows.length);

  const processedRows: NetworkRow[] = useMemo(() => {
    if (!useTransformer) {
      return rows;
    }
    return rows.map((row) => {
      const values: Record<string, number | null> = {};
      Object.entries(row.values ?? {}).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          values[key] = null;
        } else {
          values[key] = value * transformerRatio;
        }
      });
      return { ...row, values };
    });
  }, [rows, transformerRatio, useTransformer]);

  const selectedKeys = useMemo(() => {
    const group = parameterGroups[parameterGroup];
    if (!group) {
      return parameterGroups.all.keys;
    }
    if (!showLineVoltage) {
      return group.keys.filter((key) => !["uab", "ubc", "uac"].includes(key));
    }
    return group.keys;
  }, [parameterGroup, showLineVoltage]);

  const availableKeys = selectedKeys.filter((key) => labels[key]);

  const columns: ColumnsType<NetworkRow> = useMemo(() => {
    const base: ColumnsType<NetworkRow> = [
      {
        title: "Время",
        dataIndex: "timestamp",
        width: 200,
        render: (value: string) => dayjs(value).format("DD.MM.YYYY HH:mm"),
      },
    ];
    availableKeys.forEach((key) => {
      base.push({
        title: labels[key] ?? key,
        dataIndex: ["values", key],
        render: (_value, row) => formatNumber(row.values?.[key] ?? null),
        width: 140,
      });
    });
    return base;
  }, [availableKeys, labels]);

  const handlePeriodChange = (range: PeriodRange) => {
    const base = range.from ? range.from.startOf("day") : dayjs().startOf("day");
    setPeriod({ from: base, to: base.add(1, "day") });
  };

  const ready =
    regionID !== undefined &&
    objectID !== undefined &&
    selectedChannel !== undefined &&
    fromISO &&
    toISO;
  const exportUrl = ready
    ? buildExportUrl("/api/reports/network", {
        region: regionID,
        object: objectID,
        channel: selectedChannel,
        from: fromISO,
        to: toISO,
        parameter: parameterGroup,
        use_profile: useProfile || undefined,
      })
    : undefined;
  const currentObject = useMemo(
    () => objects.find((obj) => obj.n_ob === objectID && obj.syb_rnk === regionID),
    [objects, objectID, regionID],
  );
  const channelLabel =
    channelOptions.find((option) => option.value === selectedChannel)?.label ??
    (selectedChannel ? `Счётчик №${selectedChannel}` : "—");
  const defaultEmail = useDefaultEmail();
  const { openComposer, mailModal, sendDisabled } = useMailComposer({ defaultEmail, exportUrl });
  const togglesLabel =
    [
      useTransformer ? "С учётом Кт×Кн" : null,
      showLineVoltage ? "Линейные напряжения" : null,
      useProfile ? "Профильные данные" : null,
    ]
      .filter(Boolean)
      .join(", ") || "Базовые параметры";

  const handleSendMail = useCallback(() => {
    if (!ready) {
      return;
    }
    const objectLabel = currentObject?.txt
      ? `${currentObject.txt} (№${currentObject.n_ob})`
      : objectID
        ? `Объект №${objectID}`
        : "Объект";
    const range = `${dayjs(fromISO).format("DD.MM.YYYY HH:mm")} — ${dayjs(toISO).format("DD.MM.YYYY HH:mm")}`;
    openComposer({
      subject: `Параметры сети — ${objectLabel}`,
      body: `Отчёт: Параметры электросети\nОбъект: ${objectLabel}\nСчётчик: ${channelLabel}\nПериод: ${range}\nГруппа параметров: ${parameterGroups[parameterGroup]?.label ?? parameterGroup}\nОпции: ${togglesLabel}\n\nВо вложении — XLSX.`,
      filename: `network_${selectedChannel ?? "channel"}_${dayjs(fromISO).format("YYYYMMDD")}.xlsx`,
    });
  }, [
    ready,
    currentObject,
    objectID,
    channelLabel,
    fromISO,
    toISO,
    parameterGroup,
    togglesLabel,
    openComposer,
    selectedChannel,
  ]);

  return (
    <PageLayoutWithHelp
      title="Параметры электросети"
      extra={<ExportButton url={exportUrl} onSend={handleSendMail} sendDisabled={sendDisabled} />}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              style={{ minWidth: 320, flex: 1 }}
            />
            <ChannelSelector
              options={channelOptions}
              value={selectedChannel}
              loading={channelsQuery.isLoading}
              onChange={(value) => setSelectedChannel(value)}
            />
            <Select
              style={{ width: 220 }}
              value={parameterGroup}
              onChange={(value) => setParameterGroup(value)}
              options={Object.entries(parameterGroups).map(([value, info]) => ({
                label: info.label,
                value,
              }))}
            />
          </FilterRow>
          <FilterRow>
            <ToggleButtonGroup options={toggleOptions} value={toggles} onChange={setToggles} />
          </FilterRow>
          <div style={{ width: "100%" }}>
            <PeriodFilter
              value={period}
              onChange={handlePeriodChange}
              showMonthControls={false}
              showRangeControls={false}
            />
          </div>
        </FilterBar>
        {!ready && (
          <Alert
            type="info"
            showIcon
            title="Выберите объект и счётчик, чтобы загрузить параметры сети."
            style={{ maxWidth: 600 }}
          />
        )}
        {ready && (
          <EnterpriseTable<NetworkRow>
            rowKey={(row) => row.timestamp}
            columns={columns}
            dataSource={processedRows}
            loading={networkQuery.isLoading}
            pagination={pagination}
            scroll={{ x: 1200 }}
            size="small"
            toolbar={{ reload: true, onReload: () => networkQuery.refetch() }}
          />
        )}
        {mailModal}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default ReportsNetworkPage;
